import React, {useContext, useState} from 'react'
import AppContext from '../appContext'
import GeoMap, {SamplePoint} from './GeoMap'
import ApplicationDataSidebar, {AmendmentType, YearOption, SampleValue} from './ApplicationDataSidebar'
import {useInterpolation} from '../controllers/useInterpolation'
import InterpolationAlgorithmType from '../controllers/InterpolationAlgorithmType'
import Loader from './Loader'
import SampleDataOverlay from './SampleDataOverlay'
import LimeProgramLabInputRow from '../controllers/LimeProgramLabInputRow'
import LabInputUnit from '../controllers/LabInputUnit'
import {isMeq100g} from '../pages/liming/limeSoilUtils'
import {customUnits, unitCustom} from '../pages/program/CustomUnits'
import MeasurementUnit from '../controllers/MeasurementUnit'

export interface ApplicationDataPanelProps {
    samplePoints: SamplePoint[]
    sampleValues: SampleValue[]
    boundaryCoordinates?: Array<{x: number; y: number}>
    amendmentType: AmendmentType
    yearOption: YearOption
    algorithm: InterpolationAlgorithmType
    onAmendmentChange: (type: AmendmentType) => void
    onYearChange: (year: YearOption) => void
    onAlgorithmChange: (algorithm: InterpolationAlgorithmType) => void
    height?: string
    boundaryError?: boolean
    landId?: number
    programId?: number
    labInputRows?: LimeProgramLabInputRow[]
    labInputUnit?: LabInputUnit
}

/**
 * Combined map and sidebar panel for application data visualization.
 * Shows sample points on map with values in sidebar based on selected amendment/year.
 * Includes IDW interpolation overlay when programId is provided.
 */
const ApplicationDataPanel: React.FC<ApplicationDataPanelProps> = ({
    samplePoints,
    sampleValues,
    boundaryCoordinates,
    amendmentType,
    yearOption,
    algorithm,
    onAmendmentChange,
    onYearChange,
    onAlgorithmChange,
    height = "60vh",
    boundaryError,
    landId,
    programId,
    labInputRows,
    labInputUnit
}) => {
    const app = useContext(AppContext)
    const system = app.initial.system
    const [hoveredSampleLabel, setHoveredSampleLabel] = useState<string | null>(null)
    const [selectedLabel, setSelectedLabel] = useState<string | null>(null)

    const meq100gMode = labInputUnit ? isMeq100g(labInputUnit) : false
    const massPerAreaUnit = unitCustom(system, customUnits.kgha_lbAc)
    const toDisplayRate = (value: number): number =>
        system === MeasurementUnit.METRIC ? value : customUnits.kgha_lbAc.metric.invert(value)

    // Fetch interpolation
    const {imageUrl: rasterUrl, metadata: rasterMetadata, loading: rasterLoading, error: rasterError} = useInterpolation(
        programId,
        amendmentType,
        yearOption,
        algorithm,
        !!programId // Only enabled when programId is available
    )

    const hasBoundary = boundaryCoordinates && boundaryCoordinates.length > 0
    const hasRaster = !!rasterUrl && rasterMetadata && rasterMetadata.extent.length === 4
    const noInterpolationData = !rasterLoading && !hasRaster && !rasterError && !!programId

    return (
        <div>
            <div className="flex" style={{height}}>
                <div className="flex-1 relative">
                    {rasterLoading && <Loader overlay />}
                    {/* Legend overlay */}
                    {hasRaster && rasterMetadata && (() => {
                        const min = toDisplayRate(rasterMetadata.minValue)
                        const max = toDisplayRate(rasterMetadata.maxValue)
                        const range = max - min

                        // If range is very small (uniform values), show simplified legend
                        if (range < 10) {
                            const displayValue = Math.round(min)
                            return (
                                <div className="absolute top-2 right-2 z-10 bg-white/95 rounded shadow-lg p-2 text-xs">
                                    <div className="font-semibold mb-1 text-gray-700">{massPerAreaUnit}</div>
                                    <div className="flex items-center gap-1">
                                        <div
                                            className="w-4 h-3 border border-gray-300"
                                            style={{backgroundColor: '#00ff00'}}
                                        ></div>
                                        <span className="text-gray-600">{displayValue}</span>
                                    </div>
                                    <div className="text-gray-400 mt-1 text-[10px]">Uniform value</div>
                                </div>
                            )
                        }

                        const colors = [
                            '#00ff00',
                            '#adff2f',
                            '#ffff00',
                            '#ffa500',
                            '#ff4500',
                            '#ff0000',
                            '#ff00ff',
                            '#9400d3',
                            '#4b0082',
                        ]
                        // Generate tick values from min to max, using ceiling for max to ensure it's included
                        const ticks = colors.map((_, i) => {
                            if (i === colors.length - 1) return Math.ceil(max) // Ensure max is shown correctly
                            return Math.round(min + (i / (colors.length - 1)) * range)
                        })
                        return (
                            <div className="absolute top-2 right-2 z-10 bg-white/95 rounded shadow-lg p-2 text-xs">
                                <div className="font-semibold mb-1 text-gray-700">{massPerAreaUnit}</div>
                                <div className="flex flex-col-reverse gap-px">
                                    {colors.map((color, i) => (
                                        <div key={i} className="flex items-center gap-1">
                                            <div
                                                className="w-4 h-3 border border-gray-300"
                                                style={{backgroundColor: color}}
                                            ></div>
                                            <span className="text-gray-600 w-8 text-right">{ticks[i]}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })()}
                    <GeoMap
                        boundaryCoordinates={boundaryCoordinates}
                        samplePoints={samplePoints}
                        height="100%"
                        highlightedPointLabel={selectedLabel || hoveredSampleLabel}
                        onPointClick={(point) => {
                            const label = point.label || null
                            setSelectedLabel(label === selectedLabel ? null : label)
                        }}
                        onPointHover={(point) => setHoveredSampleLabel(point.label || null)}
                        onPointLeave={() => setHoveredSampleLabel(null)}
                        rasterUrl={rasterUrl}
                        rasterExtent={rasterMetadata?.extent}
                        showRaster={hasRaster || undefined}
                    />
                    {selectedLabel && labInputRows && (() => {
                        const selectedRow = labInputRows.find(r => (r.referenceNr || r.landRef) === selectedLabel)
                        if (!selectedRow) return null
                        const sampleValue = sampleValues.find(sv => sv.label === selectedLabel)

                        return (
                            <SampleDataOverlay
                                row={selectedRow}
                                meq100gMode={meq100gMode}
                                system={system}
                                extraColumns={[{
                                    header: massPerAreaUnit,
                                    row: () => sampleValue?.value != null ? sampleValue.value.toLocaleString(undefined, {maximumFractionDigits: 0}) : '-'
                                }]}
                            />
                        )
                    })()}
                </div>
                <ApplicationDataSidebar
                    sampleValues={sampleValues}
                    amendmentType={amendmentType}
                    yearOption={yearOption}
                    algorithm={algorithm}
                    onAmendmentChange={onAmendmentChange}
                    onYearChange={onYearChange}
                    onAlgorithmChange={onAlgorithmChange}
                    hoveredSampleLabel={hoveredSampleLabel}
                    onSampleHover={setHoveredSampleLabel}
                    onSampleLeave={() => setHoveredSampleLabel(null)}
                    selectedSampleLabel={selectedLabel}
                    onSampleClick={(label) => setSelectedLabel(label === selectedLabel ? null : label)}
                />
            </div>
            <div className="mt-2 text-sm text-gray-600 flex flex-wrap items-center gap-4">
                {boundaryError && (
                    <span className="text-red-500">
                        {app.word('boundary_load_error')}
                    </span>
                )}
                {hasBoundary && (
                    <span className="flex items-center">
                        <span className="inline-block w-3 h-3 bg-primary-500 mr-1 border border-primary-700"></span>
                        {app.word('boundary')}
                    </span>
                )}
                {landId && !boundaryError && !hasBoundary && (
                    <span className="text-orange-500">
                        {app.word('no_boundary_data')}
                    </span>
                )}
                {noInterpolationData && (
                    <span className="text-orange-500">
                        {app.word('no_interpolation_data')}
                    </span>
                )}
            </div>
        </div>
    )
}

export default ApplicationDataPanel
